import { useMemo } from 'react';
import type { VariantOf } from 'variant';
import type { SelectGroup, SelectOption } from '@wix/cli-ui-kit';
import type { TFunction } from '@wix/cli-i18n';
import { useTranslation } from '@wix/cli-i18n';
import { match, types, variant } from 'variant';
import { DevCenterComponent } from '../../dev-center/components/dev-center-component.js';

const DevCenterGenerateFlow = variant(types(DevCenterComponent));

export type GenerateFlow = VariantOf<typeof DevCenterGenerateFlow>;

type GenerateFlowKey =
  | 'dashboard-page'
  | 'dashboard-modal'
  | 'dashboard-plugin'
  | 'dashboard-menu-plugin'
  | 'custom-element'
  | 'embedded-script'
  | 'site-plugin'
  | 'api'
  | 'event'
  | 'service-plugin';

const getGenerateFlowByKey = (key: GenerateFlowKey, t: TFunction) => {
  return match(key, {
    'dashboard-page': () => ({
      title: t('generate_command.flow.dashboard_page'),
      value: DevCenterGenerateFlow.DashboardPage(),
    }),
    'dashboard-modal': () => ({
      title: t('generate_command.flow.dashboard_modal'),
      value: DevCenterGenerateFlow.DashboardModal(),
    }),
    'dashboard-plugin': () => ({
      title: t('generate_command.flow.dashboard_plugin'),
      value: DevCenterGenerateFlow.DashboardPlugin(),
    }),
    'dashboard-menu-plugin': () => ({
      title: t('generate_command.flow.dashboard_menu_plugin'),
      value: DevCenterGenerateFlow.DashboardMenuPlugin(),
    }),
    'custom-element': () => ({
      title: t('generate_command.flow.custom_element'),
      value: DevCenterGenerateFlow.CustomElement(),
    }),
    'embedded-script': () => ({
      title: t('generate_command.flow.embedded_script'),
      value: DevCenterGenerateFlow.EmbeddedScript(),
    }),
    'site-plugin': () => ({
      title: t('generate_command.flow.site_plugin'),
      value: DevCenterGenerateFlow.SitePlugin(),
    }),
    api: () => ({
      title: t('generate_command.flow.api.title'),
      value: DevCenterGenerateFlow.Api(),
    }),
    event: () => ({
      title: t('generate_command.flow.event'),
      value: DevCenterGenerateFlow.Event(),
    }),
    'service-plugin': () => ({
      title: t('generate_command.flow.service_plugin'),
      value: DevCenterGenerateFlow.ServicePlugin(),
    }),
  });
};

export const generateFlowKeyToSelectOption = (
  key: GenerateFlowKey,
  t: TFunction
): SelectOption<GenerateFlow> => {
  const { title, value } = getGenerateFlowByKey(key, t);
  return {
    key,
    title,
    value,
  };
};

export const useGenerateFlows = (): Array<SelectGroup<GenerateFlow>> => {
  const { t } = useTranslation();

  return useMemo(() => {
    return [
      {
        title: t('generate_command.flow.dashboard_category'),
        items: [
          // Uncomment after SidebarCategory is open to public
          // {
          //   key: 'sidebar-category',
          //   title: t('generate_command.flow.sidebar_category'),
          //   value: DevCenterGenerateFlow.DashboardSidebarCategory(),
          // },
          generateFlowKeyToSelectOption('dashboard-page', t),
          generateFlowKeyToSelectOption('dashboard-modal', t),
          generateFlowKeyToSelectOption('dashboard-plugin', t),
          generateFlowKeyToSelectOption('dashboard-menu-plugin', t),
        ],
      },
      {
        title: t('generate_command.flow.site_category'),
        items: [
          generateFlowKeyToSelectOption('custom-element', t),
          generateFlowKeyToSelectOption('embedded-script', t),
          generateFlowKeyToSelectOption('site-plugin', t),
        ],
      },
      {
        title: t('generate_command.flow.backend_category'),
        items: [
          generateFlowKeyToSelectOption('api', t),
          generateFlowKeyToSelectOption('event', t),
          generateFlowKeyToSelectOption('service-plugin', t),
        ],
      },
    ];
  }, [t]);
};
